import React, { useState, useMemo } from 'react';
import ibsStore from '../Stores/IbsSurveyStore';
import { Button } from 'reactstrap';

//The SurveyElement component is used to render input questions (ie: textinput, numimput, calculated, textarea) 
import SurveyPage from '../SurveyPage';
import SurveyTitleArea from '../SurveyTitleArea';
import SurveyContentContainer from '../SurveyContentContainer'

import { FileDownloader } from '../../helpers';
import { isLGPS } from '../../ibsHelper';
import { getTemplateData, getTemplateFile, uploadDataTemplateFiles } from '../../Services/IBS/IbsService';
import { ExistingMandatesTable } from './ExistingMandatesTable';
import { InProcessMandatesTable } from './InProcessMandatesTable';
import { observer } from 'mobx-react-lite';
import { toast } from "react-toastify";

const inProcessColumns = [
    {
        'title': 'Mandate ID',
        'id': 'mandateId',
        'width': '3',
        'filterable': true,
        'dataType': 'number',
        'tooltip': <div className="genericTooltip">
            <div>Depending on the Status, this could be NA (normally for Failed), a number (based on a user provided or guessed actual mandate ID), or word Link (no ID is provided or guessed). If Link or a number that contains a link is shown, you may click the link to search and find the mandate that corresponds to the entry. If mandate ID provided or guessed is not correct and no mandate exists to link, open Link and click Unlink. The mandate will become a new mandate: Adding(plus) such a mandate will create a completely new entry in the database with a new mandate ID.</div>
            <br />
            <div>If entry is linked to an actual mandate, the description fields in this table such as Asset, Style, Manager, and so on, will be ignored since the linked mandate already has those fields. Description fields in the linked actual mandate can be updated via Assets page.</div>
            <br />
            <div>Compare to another mandate via Link dialog: search/select an existing mandate and click Compare to show data from the template and two years of data for the selected mandate.</div>

        </div>
    },
    {
        'title': 'AgrID',
        'id': 'agrId',
        'width': '6',
        'filterable': true,
        'dataType': 'agrId',
        'tooltip': <div className="genericTooltip">
            <div>
                Search/select AgrID that best describes the entry. Asset and Style will be updated automatically. If AgrID is not yet known, but the entry should be added to the main database (Existing or uploaded), select AgrID as Unknown.
            </div>
        </div>
    },
    {
        'title': 'Asset',
        'id': 'asset',
        'width': '14',
        'filterable': true,
        'dataType': 'string',
        'tooltip': <div className="genericTooltip">
            <div>
                If entry is editable, search/select Asset class that best describes the entry. Style drop down will be updated automatically to show only those styles that are relevant to the selected Asset. If Asset is not yet known, but the entry should be added to the main database (Existing or uploaded), select Unknown. If Asset is set to Unknown, then Style should also be set to Unknown.
            </div>
        </div>
    },
    {
        'title': 'Style',
        'id': 'style',
        'width': '9',
        'filterable': true,
        'dataType': 'string',
        'tooltip': <div className="genericTooltip">
            <div>
                If entry is editable, after selecting the Asset, search/select Style that best describes the entry. Style cannot be Unknown if a valid Asset was selected.
            </div>
        </div>
    },
    {
        'title': 'Normalized Manager Name',
        'id': 'normalizedManagerName',
        'width': '14',
        'filterable': true,
        'dataType': 'string',
        'tooltip': <div className="genericTooltip">
            <div>
                Name cleaning
            </div>
        </div>
    },
    {
        'title': 'Manager',
        'id': 'managerName',
        'width': '14',
        'filterable': true,
        'dataType': 'string',
        'tooltip': <div className="genericTooltip">
            <div>If entry is editable, the Manager name can be revised.</div>
            <br />
            <div>Name cleaning: if this is a completely new mandate, consider cleaning the manager name. The clean name will be saved to the description table and be visible on the survey and reports. The full name from the template will be saved in the archive table, where all non-empty template fields are saved.</div>
        </div>
    },
    {
        'title': 'Fund',
        'id': 'fundName',
        'width': '14',
        'filterable': true,
        'dataType': 'string',
        'tooltip': <div className="genericTooltip">
            <div>If entry is editable, the Fund name can be revised.</div>
            <br />
            <div>Name cleaning: if this is a completely new mandate, consider cleaning the fund name. The clean name will be saved to the description table and be visible on the survey and reports. The full name from the template will be saved in the archive table, where all non-empty template fields are saved.</div>
        </div>
    },
    {
        'title': 'LGPS option',
        'id': 'isLgpsPoolOption',
        'width': '3',
        'filterable': true,
        'dataType': 'bool',
        'tooltip': <div className="genericTooltip">
            <div>
                Is the entry / mandate a part of an LGPS pool?
            </div>
        </div>
    },
    {
        'title': 'Stats',
        'id': 'statsInfo',
        'width': '4',
        'tooltip': <div className="genericTooltip">
            <div>Shows the number of fields collected from the template compared to the total number of fields in the relevant master template (Template type). 20 (30) means that 20 fields were collected, while 30 fields are available in the relevant master template, and 10 fields are not provided. </div>
            <br />
            <div>Click on the link, e.g. 20 (30), to review the scraped and finalized data for this template. </div>
        </div>
    },
    {
        'title': 'Template Type',
        'id': 'templateType',
        'width': '4',
        'filterable': true,
        'dataType': 'string',
        'tooltip': <div className="genericTooltip">
            <div>
                Identified template type / ID based on the comparison of the processed data template with the list of master templates. If template type cannot be identified, the entry will be marked as Failed. 
            </div>
        </div>
    },
    {
        'title': 'File',
        'id': 'filePath',
        'width': '4',
        'tooltip': <div className="genericTooltip">
            <div>When files are processed, they are split into separate worksheets (if more than 1 sheet per data template) or rows (for feed templates). The processed file contains only one mandate. The file name will include the original file name plus worksheet name if there was more than 1 worksheet and row number if it was a feed template. OriginaName [SheetName] ROWrownumber.</div>
            <br />
            <div>IMPORTANT: since the original files with multiple mandates are split into files with one mandate only, after the upload, do not use the original files but only those that you can download via the Get File links. Download the file, correct the issue, remove the incorrect row, and upload the correct mandate.</div>
            <br />
            <div>After download files, you may need to click Enable Editing or F9 in Excel if the workbook contains formulas. </div>
        </div>,
        'tooltip2': <div className="genericTooltip">
            <div>The processed file contains only one mandate per file. Name of the file is:</div>
            <div>PlanID_DataYear_MandateID_AgrID_Date_Time.</div>
        </div>,
    },
    {
        'title': 'Upload date',
        'id': 'dateAdded',
        'width': '5',
        'filterable': true,
        'dataType': 'date',
    },
    {
        'title': 'Notes',
        'id': 'analystComments',
        'width': '4',
        'centered': true,
        'tooltip': <div className="genericTooltip">
            <div>
                Analyst comments are added via the template or from here. If a comment is available, the icon will be filled with color. To remove the comment, clear the text in the dialog box and save. When the mandate is added to the database (Existing or uploaded), the comment (if it is available) will be copied as well.
            </div>
        </div>,
        'tooltip2': <div className="genericTooltip">
            <div>When a mandate is Added from ‘User action required’ section, messages generated by the tool will be added to the notes.</div>
            <br />
            <div>Notes are not available if mandate was not added via the tool but only via Survey interface.</div>
        </div>
    },
    {
        'title': 'Status',
        'id': 'status',
        'width': '7',
        'filterable': true,
        'dataType': 'string',
        'tooltip': <div className="genericTooltip">
            <div>There are two main statuses: failed and non-failed. Failed are those mandates/templates that could not be properly processed for any reason. The status will contain info on possible issues. Download the file, review the issue, fix it, remove the incorrect mandate row, and re-upload the fixed downloaded file. DO NOT upload the original file as it may contain more than one sheet or could be a feed. In that case, it will result in duplicating already uploaded mandates.</div>
            <br />
            <div>In non-failed cases, the status may contain words:</div>
            <div>- Guessed mandate from: mandate ID was guessed based on various fields.</div>
            <div>- Data exist: non-empty mandate possibly available in the database (Existing or uploaded).</div>
            <div>- Duplicate: mandate possibly available in the User action required.</div>
            <div>- New / not mapped: mandate ID could not be found.</div>
            <br />
            <div>Depending on whether mandate ID is available and is correct, choose Add (plus) or Remove (minus) in the Action field, or click on the mandate ID or Link to link the mandate to the correct mandate in the database.</div>
        </div>,
        'tooltip2': <div className="genericTooltip">
            <div>- Uploaded - non-empty mandate was likely uploaded via this tool and template file is available for download.</div>
            <div>- Survey - non-empty mandate was likely added via the survey interface or directly to the underlying database table. No file was uploaded.</div>
            <div>- No status - only an empty row exists for this mandate in the database. No data was uploaded or added.</div>
        </div>
    },
    {
        'title': 'Clean',
        'id': 'isClean',
        'width': '4',
        'centered': true,
        'tooltip2': <div className="genericTooltip">
            <div>Check this box if the mandate is clean</div>
        </div>
    },
    {
        'title': 'Action',
        'id': '',
        'width': '5',
        'tooltip': <div className="genericTooltip">
            <div>Add (plus) will copy data into the database (Existing or uploaded) and remove row from User action required. The existing mandate (with the same mandate ID) in the database will be completely overwritten including analyst comments and the file. Note that adding will not always result in copying data to the Existing and may end up again in User action required in case there was an issue in copying the data.</div>
            <br />
            <div>Remove (minus) will permanently remove the current row with the file from the User action required. Failed rows only have the remove button.</div>
            <br />
            <div>It is possible to run multiple actions at the same time. For that, select the respective check boxes on the right side of the buttons and then click ‘Run all actions’ button. To select all Add or all Remove, click on Add(plus)/Remove(minus) on the top of the User action required.</div>
            <br />
            <div>Do not remove failed entries unless they are corrected: file downloaded, issue fixed, failed entry removed, and corrected file is re-uploaded.</div>
        </div>,
        'tooltip2': <div className="genericTooltip">
            <div>Delete mandate from the database. This is different from the delete on the Assets page.</div>            
            <div>- If mandate has historical data not just current year, then only that year’s data and files will be deleted.</div>
            <div>- If mandate exists only in the current survey, then the mandate description from DbMandatesInfo table will be deleted as well. </div>
        </div>
    },
]
const existingColumns = [
    {
        'title': 'Mandate ID',
        'id': 'mandateId',
        'width': '6',
        'filterable': true,
        'dataType': 'number',
        'tooltip': <div className="genericTooltip">
            <div>Depending on the Status, this could be NA (normally for Failed), a number (based on a user provided or guessed actual mandate ID), or word Link (no ID is provided or guessed). If Link or a number that contains a link is shown, you may click the link to search and find the mandate that corresponds to the entry. If mandate ID provided or guessed is not correct and no mandate exists to link, open Link and click Unlink. The mandate will become a new mandate: Adding(plus) such a mandate will create a completely new entry in the database with a new mandate ID.</div>
            <br />
            <div>If entry is linked to an actual mandate, the description fields in this table such as Asset, Style, Manager, and so on, will be ignored since the linked mandate already has those fields. Description fields in the linked actual mandate can be updated via Assets page.</div>
            <br />
            <div>Compare to another mandate via Link dialog: search/select an existing mandate and click Compare to show data from the template and two years of data for the selected mandate.</div>

        </div>
    },
    {
        'title': 'AgrID',
        'id': 'agrId',
        'width': '6',
        'filterable': true,
        'dataType': 'agrId',
        'tooltip': <div className="genericTooltip">
            <div>
                Search/select AgrID that best describes the entry. Asset and Style will be updated automatically. If AgrID is not yet known, but the entry should be added to the main database (Existing or uploaded), select AgrID as Unknown.
            </div>
        </div>
    },
    {
        'title': 'NAV ($M)',
        'id': 'holdingNavAvg',
        'width': '6',
        'filterable': true,
        'dataType': 'number',
        'tooltip': <div className="genericTooltip">
            <div>
                HoldingNavAvg
            </div>
        </div>
    },
    {
        'title': 'Cost (bps)',
        'id': 'costBps',
        'width': '6',
        'filterable': true,
        'dataType': 'number',
        'tooltip': <div className="genericTooltip">
            <div>
                Calculated from (manager cost + performance cost + underlying manager cost + underlying performance cost + internal cost) / HoldingNavAvg.
            </div>
        </div>
    },
    {
        'title': 'Normalized Manager Name',
        'id': 'normalizedManagerName',
        'width': '8',
        'filterable': true,
        'dataType': 'string',
        'tooltip': <div className="genericTooltip">
            <div>If entry is editable, the Manager name can be revised.</div>
            <br />
            <div>Name cleaning: if this is a completely new mandate, consider cleaning the manager name. The clean name will be saved to the description table and be visible on the survey and reports. The full name from the template will be saved in the archive table, where all non-empty template fields are saved.</div>
        </div>
    },
    {
        'title': 'Manager',
        'id': 'managerName',
        'width': '14',
        'filterable': true,
        'dataType': 'string',
        'tooltip': <div className="genericTooltip">
            <div>If entry is editable, the Manager name can be revised.</div>
            <br />
            <div>Name cleaning: if this is a completely new mandate, consider cleaning the manager name. The clean name will be saved to the description table and be visible on the survey and reports. The full name from the template will be saved in the archive table, where all non-empty template fields are saved.</div>
        </div>
    },
    {
        'title': 'Fund',
        'id': 'fundName',
        'width': '19',
        'filterable': true,
        'dataType': 'string',
        'tooltip': <div className="genericTooltip">
            <div>If entry is editable, the Fund name can be revised.</div>
            <br />
            <div>Name cleaning: if this is a completely new mandate, consider cleaning the fund name. The clean name will be saved to the description table and be visible on the survey and reports. The full name from the template will be saved in the archive table, where all non-empty template fields are saved.</div>
        </div>
    },
    {
        'title': 'LGPS option',
        'id': 'isLgpsPoolOption',
        'width': '6',
        'filterable': true,
        'dataType': 'bool',
        'tooltip': <div className="genericTooltip">
            <div>
                Is the entry / mandate a part of an LGPS pool?
            </div>
        </div>
    },
    {
        'title': 'Stats',
        'id': 'statsInfo',
        'width': '4',
        'tooltip': <div className="genericTooltip">
            <div>Shows the number of fields collected from the template compared to the total number of fields in the relevant master template (Template type). 20 (30) means that 20 fields were collected, while 30 fields are available in the relevant master template, and 10 fields are not provided. </div>
            <br />
            <div>Click on the link, e.g. 20 (30), to review the scraped and finalized data for this template. </div>
        </div>
    },
    {
        'title': 'Template Type',
        'id': 'templateType',
        'width': '5',
        'filterable': true,
        'dataType': 'string',
        'tooltip': <div className="genericTooltip">
            <div>
                Identified template type / ID based on the comparison of the processed data template with the list of master templates. If template type cannot be identified, the entry will be marked as Failed.
            </div>
        </div>
    },
    {
        'title': 'File',
        'id': 'filePath',
        'width': '4',
        'tooltip': <div className="genericTooltip">
            <div>When files are processed, they are split into separate worksheets (if more than 1 sheet per data template) or rows (for feed templates). The processed file contains only one mandate. The file name will include the original file name plus worksheet name if there was more than 1 worksheet and row number if it was a feed template. OriginaName [SheetName] ROWrownumber.</div>
            <br />
            <div>IMPORTANT: since the original files with multiple mandates are split into files with one mandate only, after the upload, do not use the original files but only those that you can download via the Get File links. Download the file, correct the issue, remove the incorrect row, and upload the correct mandate.</div>
            <br />
            <div>After download files, you may need to click Enable Editing or F9 in Excel if the workbook contains formulas. </div>
        </div>,
        'tooltip2': <div className="genericTooltip">
            <div>The processed file contains only one mandate per file. Name of the file is:</div>
            <div>PlanID_DataYear_MandateID_AgrID_Date_Time.</div>
        </div>,
    },
    {
        'title': 'Upload date',
        'id': 'dateAdded',
        'width': '6',
        'filterable': true,
        'dataType': 'date',
    },
    {
        'title': 'Notes',
        'id': 'analystComments',
        'width': '4',
        'centered': true,
        'tooltip': <div className="genericTooltip">
            <div>
                Analyst comments are added via the template or from here. If a comment is available, the icon will be filled with color. To remove the comment, clear the text in the dialog box and save. When the mandate is added to the database (Existing or uploaded), the comment (if it is available) will be copied as well.
            </div>
        </div>,
        'tooltip2': <div className="genericTooltip">
            <div>When a mandate is Added from ‘User action required’ section, messages generated by the tool will be added to the notes.</div>
            <br />
            <div>Notes are not available if mandate was not added via the tool but only via Survey interface.</div>
        </div>
    },
    {
        'title': 'Status',
        'id': 'status',
        'width': '5',
        'filterable': true,
        'dataType': 'string',
        'tooltip': <div className="genericTooltip">
            <div>There are two main statuses: failed and non-failed. Failed are those mandates/templates that could not be properly processed for any reason. The status will contain info on possible issues. Download the file, review the issue, fix it, remove the incorrect mandate row, and re-upload the fixed downloaded file. DO NOT upload the original file as it may contain more than one sheet or could be a feed. In that case, it will result in duplicating already uploaded mandates.</div>
            <br />
            <div>In non-failed cases, the status may contain words:</div>
            <div>- Guessed mandate from: mandate ID was guessed based on various fields.</div>
            <div>- Data exist: non-empty mandate possibly available in the database (Existing or uploaded).</div>
            <div>- Duplicate: mandate possibly available in the User action required.</div>
            <div>- New / not mapped: mandate ID could not be found.</div>
            <br />
            <div>Depending on whether mandate ID is available and is correct, choose Add (plus) or Remove (minus) in the Action field, or click on the mandate ID or Link to link the mandate to the correct mandate in the database.</div>
        </div>,
        'tooltip2': <div className="genericTooltip">
            <div>- Uploaded - non-empty mandate was likely uploaded via this tool and template file is available for download.</div>
            <div>- Survey - non-empty mandate was likely added via the survey interface or directly to the underlying database table. No file was uploaded.</div>
            <div>- No status - only an empty row exists for this mandate in the database. No data was uploaded or added.</div>
        </div>
    },
    {
        'title': 'Clean',
        'id': 'isClean',
        'width': '4',
        'filterable': true,
        'dataType': 'bool',
        'centered': true,
        'tooltip2': <div className="genericTooltip">
            <div>Check this box if the mandate is clean</div>
        </div>
    },
    {
        'title': 'Action',
        'id': '',
        'width': '5',
        'tooltip': <div className="genericTooltip">
            <div>Add (plus) will copy data into the database (Existing or uploaded) and remove row from User action required. The existing mandate (with the same mandate ID) in the database will be completely overwritten including analyst comments and the file. Note that adding will not always result in copying data to the Existing and may end up again in User action required in case there was an issue in copying the data.</div>
            <br />
            <div>Remove (minus) will permanently remove the current row with the file from the User action required. Failed rows only have the remove button.</div>
            <br />
            <div>It is possible to run multiple actions at the same time. For that, select the respective check boxes on the right side of the buttons and then click ‘Run all actions’ button. To select all Add or all Remove, click on Add(plus)/Remove(minus) on the top of the User action required.</div>
            <br />
            <div>Do not remove failed entries unless they are corrected: file downloaded, issue fixed, failed entry removed, and corrected file is re-uploaded.</div>
        </div>,
        'tooltip2': <div className="genericTooltip">
            <div>Delete mandate from the database. This is different from the delete on the Assets page.</div>
            <div>- If mandate has historical data not just current year, then only that year’s data and files will be deleted.</div>
            <div>- If mandate exists only in the current survey, then the mandate description from DbMandatesInfo table will be deleted as well. </div>
        </div>
    },
]

export const Templates = observer(() => {
    window.store = ibsStore;

    let lgpsColumnIdForExisting = existingColumns.findIndex(column => column.id === 'isLgpsPoolOption');
    let lgpsColumnIdForInProcess = inProcessColumns.findIndex(column => column.id === 'isLgpsPoolOption');
    if (!isLGPS()) {
        existingColumns[lgpsColumnIdForExisting].hidden = true;
        inProcessColumns[lgpsColumnIdForInProcess].hidden = true;
    } else {
        existingColumns[lgpsColumnIdForExisting].hidden = false;
        inProcessColumns[lgpsColumnIdForInProcess].hidden = false;
    }

    let [files, setFiles] = useState([]);
    let [isUploading, setIsUploading] = useState(false);

    const downloadFile = async (row, isInProcess = false) => {
        if (row.filePath !== 'broken') {

            let blob = await getTemplateFile(row.rowId, isInProcess);
            if (blob && blob.size > 0) {
                FileDownloader(
                    URL.createObjectURL(blob),
                    `${row.filePath ? row.filePath.substring(Math.max(row.filePath.lastIndexOf("\\") + 1, 0)) : 'template.xlsx'}`
                    );
            } else {
                let toastId = null;
                toastId = toast(
                    <div>No file found on the server.</div>, {
                        position: toast.POSITION.BOTTOM_RIGHT,
                    type: toast.TYPE.ERROR,
                    draggable: false,
                    autoClose: true,
                    hideProgressBar: true,
                    className: "red-notif"
                });
                if (isInProcess) {
                    let rowIdx = ibsStore.templateInProcess.findIndex(template => template.rowId === row.rowId);
                    ibsStore.templateInProcess[rowIdx].filePath = 'broken';
                } else {
                    let rowIdx = ibsStore.templateExisting.findIndex(template => template.rowId === row.rowId);
                    ibsStore.templateExisting[rowIdx].filePath = 'broken';
                }
            }
        }
    }

    const onFileChange = (e) => {
        if (e.target.files.length > 0) {
            let totalFileSize = Object.values(e.target.files).map(file => file.size).reduce((sum, cur) => (sum + cur), 0);
            let toastId = null;
            if (totalFileSize > 20971520) {
                toastId = toast(
                    <div>Please keep the upload under 20 MB.<br />Your files size: {(totalFileSize / 1048576).toFixed(2)} MB</div>, {
                    position: toast.POSITION.BOTTOM_RIGHT,
                    type: toast.TYPE.ERROR,
                    draggable: false,
                    autoClose: true,
                    hideProgressBar: true,
                    className: "red-notif"
                });
                setFiles([]);
            } else {
                if (totalFileSize > 10485760) {
                    toastId = toast(
                        <div>You're uploading {(totalFileSize / 1048576).toFixed(2)} MB, this may take a few moments.</div>, {
                        position: toast.POSITION.BOTTOM_RIGHT,
                        type: toast.TYPE.DEFAULT,
                        draggable: false,
                        autoClose: true,
                        hideProgressBar: true,
                            className: "blue-notif"
                    });
                }
                let newFiles = [];
                for (let idx = 0; idx < e.target.files.length; idx++) {
                    newFiles.push(e.target.files[idx]);
                }
                setFiles(newFiles);
            }
        }
    }

    const confirmFiles = (e) => {
        if (e.target.files.length > files.length) {
            e.target.value = null;
        }
    }

    const fileUpload = async () => {
        if (Array.isArray(files) && files.length > 0) {
            setIsUploading(true);
            let result = await uploadDataTemplateFiles(files);
            if (result) {
                setIsUploading(false);
                getTemplateData();
            } else {
                setIsUploading(false);
                // spawn an error?
                let toastId = null;
                toastId = toast(
                    <div>There was an error uploading one of the files.</div>, {
                    position: toast.POSITION.BOTTOM_RIGHT,
                    type: toast.TYPE.ERROR,
                    draggable: false,
                    autoClose: true,
                    hideProgressBar: true,
                    className: "red-notif"
                });
            }
        }
        setFiles([]);
    }

    return (
        <React.Fragment>
            <SurveyPage >
                <SurveyTitleArea pageName="Templates" />
                <SurveyContentContainer>
                    
                    <div className="own-row">
                        <div className='uploadButtons'>
                            <Button className={'btn ' + (isUploading ? 'btn-close grey' : 'btn-add-row')} onClick={fileUpload}>{isUploading ? 'Uploading...' : 'Upload data templates'}</Button>
                            <input id="files" className="hidden" type={'file'} multiple onChange={(e) => onFileChange(e)} onClick={(e) => confirmFiles(e)}></input>
                            <label className="inputButton" htmlFor="files">Choose Files</label>
                            <div className="fileList">
                                {files.length > 0 ? files.map(file => file.name).join(', ') : "No File Selected"}
                            </div>
                        </div>
                    </div>
                    <InProcessMandatesTable
                        columns={inProcessColumns.slice()}
                        downloadFile={downloadFile}
                    />
                    <ExistingMandatesTable
                        columns={existingColumns.slice()}
                        downloadFile={downloadFile}
                    />
                    
                </SurveyContentContainer>

            </SurveyPage>
        </React.Fragment>
    );
});